import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Download,
  FileText,
  Loader2,
  PlayCircle,
  RefreshCw,
  TriangleAlert,
} from "lucide-react";
import { taxApi } from "./api";
import type { TaxDocumentRow, TaxDocStatus } from "./types";
import {
  ApplicationsTable,
  type Column,
} from "../applications/shared/ApplicationsTable";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { formatDateTime } from "../applications/shared/format";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";
import { Can } from "@/lib/permissions/useCan";
import { PERMISSIONS } from "@/lib/permissions/catalog";

const PAGE_SIZE = 25;

const CLP = (n: number) => `$${Number(n || 0).toLocaleString("es-CL")}`;

const STATUS_STYLE: Record<TaxDocStatus, { label: string; className: string }> = {
  pending: { label: "En cola", className: "text-info" },
  issuing: { label: "Emitiendo", className: "text-warning" },
  issued: { label: "Emitido", className: "text-success" },
  failed: { label: "Fallido", className: "text-danger font-semibold" },
  voided: { label: "Anulado", className: "text-fg-subtle line-through" },
};

export function TaxDocumentsScreen() {
  const [status, setStatus] = useState("all");
  const [docType, setDocType] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<TaxDocumentRow | null>(null);

  const qc = useQueryClient();
  const { push } = useToast();

  const summaryQuery = useQuery({
    queryKey: ["tax", "summary"],
    queryFn: taxApi.summary,
  });

  const listQuery = useQuery({
    queryKey: ["tax", "documents", { status, docType, search, page }],
    queryFn: () =>
      taxApi.list({ status, docType, search, page, pageSize: PAGE_SIZE }),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["tax"] });
  };

  const retry = useMutation({
    mutationFn: (id: number) => taxApi.retry(id),
    onSuccess: (res) => {
      push({ kind: "success", message: res.message });
      invalidate();
    },
    onError: (err: any) =>
      push({
        kind: "error",
        message: err?.response?.data?.message || "No se pudo reencolar.",
      }),
  });

  const runEmitter = useMutation({
    mutationFn: () => taxApi.runEmitter(),
    onSuccess: ({ summary }) => {
      push({
        kind: "success",
        message:
          summary.scanned === 0
            ? "Emisor ejecutado. No había documentos en cola."
            : `Emitidos: ${summary.issued} · Reintentos: ${summary.retried} · Fallidos: ${summary.failed} · Diferidos: ${summary.deferred}`,
      });
      invalidate();
    },
    onError: (err: any) =>
      push({
        kind: "error",
        message: err?.response?.data?.message || "Error ejecutando el emisor.",
      }),
  });

  const download = async (id: number, kind: "pdf" | "xml") => {
    try {
      const url = await taxApi.downloadUrl(id, kind);
      window.open(url, "_blank", "noopener");
    } catch (err: any) {
      push({
        kind: "error",
        message: err?.response?.data?.message || "No se pudo generar el enlace.",
      });
    }
  };

  const summary = summaryQuery.data;
  const failedCount = summary?.counts.failed ?? 0;

  const columns: Column<TaxDocumentRow>[] = [
    {
      key: "folio",
      header: "Folio",
      width: "min",
      cell: (d) => (
        <span className="tabular-nums">
          {d.folio ?? <span className="text-fg-subtle">—</span>}
        </span>
      ),
    },
    { key: "tipo", header: "Tipo", width: "min", cell: (d) => d.docTypeLabel },
    {
      key: "status",
      header: "Estado",
      width: "min",
      cell: (d) => (
        <span className={STATUS_STYLE[d.status]?.className}>
          {STATUS_STYLE[d.status]?.label ?? d.status}
        </span>
      ),
    },
    { key: "desc", header: "Detalle", cell: (d) => d.description },
    {
      key: "receptor",
      header: "Receptor",
      cell: (d) => d.receptorEmail || <span className="text-fg-subtle">—</span>,
    },
    {
      key: "neto",
      header: "Neto",
      align: "right",
      cell: (d) => <span className="tabular-nums">{CLP(d.netAmount)}</span>,
    },
    {
      key: "iva",
      header: "IVA",
      align: "right",
      cell: (d) => <span className="tabular-nums">{CLP(d.ivaAmount)}</span>,
    },
    {
      key: "total",
      header: "Total",
      align: "right",
      cell: (d) => (
        <span className="tabular-nums font-semibold">{CLP(d.totalAmount)}</span>
      ),
    },
    {
      key: "fecha",
      header: "Emitido",
      width: "min",
      cell: (d) =>
        d.issuedAt ? (
          formatDateTime(d.issuedAt)
        ) : (
          <span className="text-fg-subtle">—</span>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <header>
        <p className="nx-eyebrow mb-1">Operaciones · Tributario</p>
        <h1 className="nx-h1">Documentos tributarios (SII)</h1>
        <p className="text-sm text-fg-muted mt-1">
          Boletas y notas de crédito que Mixcore emite por su comisión, bajo el
          RUT de <strong className="text-fg">GO UP CLOUD SPA</strong>. Un
          documento en estado <em>fallido</em> es un comprador sin su boleta.
        </p>
      </header>

      {summary && !summary.emisionActiva && (
        <div className="nx-card p-4 flex items-start gap-3 border-warning/40">
          <TriangleAlert size={18} className="text-warning shrink-0 mt-0.5" />
          <div className="text-sm text-fg-muted">
            <strong className="text-fg">
              Proveedor en modo «{summary.provider}».
            </strong>{" "}
            Los folios de esta pantalla son simulados y no existen ante el SII.
            Sirve para probar el flujo completo sin consumir folios reales.
          </div>
        </div>
      )}

      {/* Tarjetas de estado */}
      {summaryQuery.isLoading ? (
        <LoadingBlock label="Cargando resumen…" />
      ) : summaryQuery.isError ? (
        <ErrorBlock onRetry={() => summaryQuery.refetch()} />
      ) : summary ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="En cola" value={summary.counts.pending} />
          <StatCard
            label="Fallidos"
            value={failedCount}
            tone={failedCount > 0 ? "danger" : undefined}
          />
          <StatCard label="Emitidos (mes)" value={summary.mesEnCurso.documentos} />
          <StatCard
            label="IVA débito del mes"
            value={CLP(summary.mesEnCurso.ivaDebitoNeto)}
            hint={
              summary.mesEnCurso.ivaAnuladoPorNotasCredito > 0
                ? `${CLP(summary.mesEnCurso.iva)} emitido − ${CLP(summary.mesEnCurso.ivaAnuladoPorNotasCredito)} anulado`
                : "Neto de notas de crédito"
            }
          />
        </div>
      ) : null}

      {/* Filtros y acciones */}
      <div className="nx-card p-4 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="nx-eyebrow">Estado</label>
          <select
            className="nx-input"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="all">Todos</option>
            <option value="pending">En cola</option>
            <option value="issuing">Emitiendo</option>
            <option value="issued">Emitidos</option>
            <option value="failed">Fallidos</option>
            <option value="voided">Anulados</option>
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="nx-eyebrow">Tipo</label>
          <select
            className="nx-input"
            value={docType}
            onChange={(e) => {
              setDocType(e.target.value);
              setPage(1);
            }}
          >
            <option value="all">Todos</option>
            <option value="39">Boleta (39)</option>
            <option value="33">Factura (33)</option>
            <option value="61">Nota de crédito (61)</option>
          </select>
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
          <label className="nx-eyebrow">Buscar</label>
          <input
            className="nx-input"
            placeholder="Folio, correo, detalle…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <button
          onClick={() => listQuery.refetch()}
          disabled={listQuery.isFetching}
          className="nx-btn-secondary"
        >
          <RefreshCw
            size={14}
            className={listQuery.isFetching ? "animate-spin" : ""}
          />
          Recargar
        </button>

        <Can permission={PERMISSIONS.TAX_RETRY}>
          <button
            onClick={async () => {
              const ok = await confirmAction({
                title: "Ejecutar el emisor ahora",
                message:
                  "Procesa la cola sin esperar los 5 minutos del cron. Consume cuota del proveedor por cada documento emitido.",
                confirmLabel: "Ejecutar",
              });
              if (ok === true) runEmitter.mutate();
            }}
            disabled={runEmitter.isPending}
            className="nx-btn-primary"
          >
            {runEmitter.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <PlayCircle size={14} />
            )}
            Emitir ahora
          </button>
        </Can>
      </div>

      {/* Tabla */}
      {listQuery.isLoading ? (
        <LoadingBlock label="Cargando documentos…" />
      ) : listQuery.isError ? (
        <ErrorBlock onRetry={() => listQuery.refetch()} />
      ) : (
        <ApplicationsTable
          rows={listQuery.data?.documents ?? []}
          columns={columns}
          page={page}
          pageSize={PAGE_SIZE}
          total={listQuery.data?.total ?? 0}
          onPageChange={setPage}
          onRowClick={setSelected}
          selectedId={selected?.id ?? null}
          emptyLabel="No hay documentos que coincidan con los filtros."
        />
      )}

      {/* Detalle */}
      {selected && (
        <div className="nx-card p-5 flex flex-col gap-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="nx-eyebrow mb-1">
                {selected.docTypeLabel}
                {selected.folio ? ` N° ${selected.folio}` : " · sin folio"}
              </p>
              <h2 className="text-base font-semibold text-fg">
                {selected.description}
              </h2>
            </div>
            <button
              className="nx-btn-ghost"
              onClick={() => setSelected(null)}
              aria-label="Cerrar detalle"
            >
              Cerrar
            </button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <Field label="Estado" value={STATUS_STYLE[selected.status]?.label} />
            <Field label="Neto" value={CLP(selected.netAmount)} />
            <Field label="IVA" value={CLP(selected.ivaAmount)} />
            <Field label="Total" value={CLP(selected.totalAmount)} />
            <Field label="Receptor" value={selected.receptorEmail || "—"} />
            <Field label="RUT receptor" value={selected.receptorRut} />
            <Field label="Proveedor" value={selected.provider || "—"} />
            <Field label="Track ID SII" value={selected.siiTrackId || "—"} />
            <Field label="Origen" value={`${selected.sourceType} #${selected.sourceId}`} />
            <Field label="Intentos" value={String(selected.attempts)} />
            <Field
              label="Correo enviado"
              value={selected.emailSentAt ? formatDateTime(selected.emailSentAt) : "No"}
            />
            <Field
              label="Emitido"
              value={selected.issuedAt ? formatDateTime(selected.issuedAt) : "—"}
            />
          </div>

          {selected.lastError && (
            <div className="flex items-start gap-2 p-3 rounded bg-danger/10 border border-danger/30">
              <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
              <div>
                <p className="text-2xs nx-eyebrow text-danger mb-1">Último error</p>
                <p className="text-xs text-fg-muted font-mono break-all">
                  {selected.lastError}
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {selected.hasPdf && (
              <button
                className="nx-btn-secondary"
                onClick={() => download(selected.id, "pdf")}
              >
                <Download size={14} /> PDF
              </button>
            )}
            {selected.hasXml && (
              <button
                className="nx-btn-secondary"
                onClick={() => download(selected.id, "xml")}
              >
                <FileText size={14} /> XML (documento legal)
              </button>
            )}

            {selected.status === "failed" && (
              <Can permission={PERMISSIONS.TAX_RETRY}>
                <button
                  className="nx-btn-primary"
                  disabled={retry.isPending}
                  onClick={async () => {
                    const ok = await confirmAction({
                      title: "Reencolar documento",
                      message:
                        "Vuelve a la cola con los intentos en cero. Revisa antes el último error: si el documento alcanzó a emitirse en el SII, reintentarlo generaría un duplicado que hay que anular con nota de crédito.",
                      confirmLabel: "Reencolar",
                    });
                    if (ok === true) retry.mutate(selected.id);
                  }}
                >
                  {retry.isPending ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <RefreshCw size={14} />
                  )}
                  Reencolar
                </button>
              </Can>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number | string;
  hint?: string;
  tone?: "danger";
}) {
  return (
    <div className="nx-card p-4">
      <p className="nx-eyebrow mb-2">{label}</p>
      <p
        className={`text-2xl font-semibold tabular-nums ${
          tone === "danger" ? "text-danger" : "text-fg"
        }`}
      >
        {value}
      </p>
      {hint && <p className="text-2xs text-fg-subtle mt-1">{hint}</p>}
    </div>
  );
}

function Field({ label, value }: { label: string; value?: string }) {
  return (
    <div>
      <p className="nx-eyebrow mb-1">{label}</p>
      <p className="text-fg break-words">{value ?? "—"}</p>
    </div>
  );
}
